import firebase from "firebase";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import {
  makeId,
  validateVenueMenu,
  validateVenueOption,
  VenueMenu,
  VenueOption
} from "sweet-global";
import BackButton from "sweet-web/components/universal/Button/BackButton";
import NextButton from "sweet-web/components/universal/Button/NextButton";
import OutlineButton from "sweet-web/components/universal/Button/OutlineButton";
import { Label, SectionTitle } from "sweet-web/components/universal/Text";
import {
  OptionWrapper,
  Section,
  Wrapper
} from "sweet-web/components/universal/View";
import { RootState } from "sweet-web/redux";
import * as selectors from "sweet-web/redux/selectors";
import { UpdateVenue } from "sweet-web/redux/venue/actions";
import EditMenu from "./EditMenu";
import EditOption from "./EditOption";
import MenuLine from "./MenuLine";
import OptionLine from "./OptionLine";

type Props = RouteComponentProps & ConnectedProps<typeof connector>;

const Services: React.FC<Props> = props => {
  const { venueId } = props.match.params as { venueId: string };
  const { venueEntity, loading } = props;

  const [addingFoodOption, setAddingFoodOption] = useState(false);
  const [addingDrinkOption, setAddingDrinkOption] = useState(false);
  const [editingOptions, setEditingOptions] = useState([]);
  const [addingFoodMenu, setAddingFoodMenu] = useState(false);
  const [addingDrinkMenu, setAddingDrinkMenu] = useState(false);
  const [editingMenus, setEditingMenus] = useState([]);
  const [venue, setVenue] = useState(venueEntity);
  useEffect(() => {
    setVenue(venueEntity);
  }, [venueEntity]);

  const addOption = async (option: VenueOption) => {
    const newId = makeId();

    return await saveOption(newId, option);
  };

  const saveOption = async (
    optionId: string,
    option: VenueOption
  ): Promise<boolean> => {
    const validation = validateVenueOption(option);
    if (!validation.valid) {
      alert(`Please add ${validation.errors.join(", ")}`);

      return false;
    }
    setVenue(v => ({
      ...v,
      options: {
        ...v.options,
        [optionId]: option
      }
    }));
    await props.UpdateVenue(venueId, { [`options.${optionId}`]: option });

    return true;
  };

  const removeOption = async (optionId: string) => {
    setVenue(v => {
      const newOptions = Object.assign({}, v.options);
      delete newOptions[optionId];

      return {
        ...v,
        options: newOptions
      };
    });
    await props.UpdateVenue(venueId, {
      [`options.${optionId}`]: firebase.firestore.FieldValue.delete()
    });
  };

  const addMenu = async (menu: VenueMenu) => {
    const newId = makeId();

    return await saveMenu(newId, menu);
  };

  const saveMenu = async (
    menuId: string,
    menu: VenueMenu
  ): Promise<boolean> => {
    const validation = validateVenueMenu(menu);
    if (!validation.valid) {
      alert(`Please add ${validation.errors.join(", ")}`);

      return false;
    }
    setVenue(v => ({
      ...v,
      menus: {
        ...v.menus,
        [menuId]: menu
      }
    }));
    await props.UpdateVenue(venueId, { [`menus.${menuId}`]: menu });

    return true;
  };

  const removeMenu = async (menuId: string) => {
    setVenue(v => {
      const newMenus = Object.assign({}, v.menus);
      delete newMenus[menuId];

      return {
        ...v,
        menus: newMenus
      };
    });
    await props.UpdateVenue(venueId, {
      [`menus.${menuId}`]: firebase.firestore.FieldValue.delete()
    });
  };

  const next = async () => {
    props.history.push(`/listing/${venueId}/photos`);
  };
  const back = async () => {
    props.history.push(`/listing/${venueId}/spaces`);
  };

  const foodOptions = [];
  const drinkOptions = [];

  Object.keys(venue.options).forEach(optionId => {
    const option = venue.options[optionId];
    if (option.type === "FOOD") {
      foodOptions.push({
        ...option,
        id: optionId
      });
    } else if (option.type === "DRINK") {
      drinkOptions.push({
        ...option,
        id: optionId
      });
    }
  });

  const foodMenus = [];
  const drinkMenus = [];

  Object.keys(venue.menus).forEach(menuId => {
    const menu = venue.menus[menuId];
    if (menu.type === "FOOD") {
      foodMenus.push({
        ...menu,
        id: menuId
      });
    } else if (menu.type === "DRINK") {
      drinkMenus.push({
        ...menu,
        id: menuId
      });
    }
  });

  return (
    <Wrapper>
      <Section>
        <SectionTitle style={{ marginBottom: 5 }}>Drink Menus</SectionTitle>
        <Label>Upload PDFs of your drink menus for customers</Label>

        {drinkMenus.length > 0 && (
          <OptionWrapper>
            {drinkMenus.map(menu => {
              if (editingMenus.includes(menu.id))
                return (
                  <EditMenu
                    type="DRINK"
                    key={`edit-menu-${menu.id}`}
                    menuId={menu.id}
                    menuEntity={menu}
                    onSave={async revisedMenu => {
                      const saved = await saveMenu(menu.id, revisedMenu);
                      if (saved)
                        setEditingMenus(e => _.remove(e, { i: menu.id }));
                    }}
                    onCancel={() =>
                      setEditingMenus(e => _.remove(e, { i: menu.id }))
                    }
                  />
                );
              else
                return (
                  <MenuLine
                    key={`listing-drink-menu-${menu.id}`}
                    menuId={menu.id}
                    menuEntity={menu}
                    onEdit={() => setEditingMenus(e => [...e, menu.id])}
                    onRemove={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to remove this food menu?"
                        )
                      ) {
                        removeMenu(menu.id);
                      }
                    }}
                  />
                );
            })}
          </OptionWrapper>
        )}

        {!addingDrinkMenu && (
          <OutlineButton
            style={{ marginTop: 15 }}
            title="Add Drink Menu"
            onClick={() => setAddingDrinkMenu(true)}
          />
        )}
        {addingDrinkMenu && (
          <EditMenu
            type="DRINK"
            onSave={async newMenu => {
              const saved = await addMenu(newMenu);
              if (saved) setAddingDrinkMenu(false);
            }}
            onCancel={() => setAddingDrinkMenu(false)}
          />
        )}
      </Section>

      <Section>
        <SectionTitle style={{ marginBottom: 5 }}>Drink Options</SectionTitle>
        <Label>Give ballpark starting prices for your drink options</Label>

        {drinkOptions.length > 0 && (
          <OptionWrapper>
            {drinkOptions.map(option => {
              if (editingOptions.includes(option.id) && option.type == "DRINK")
                return (
                  <EditOption
                    type="DRINK"
                    key={`edit-drink-option-${option.id}`}
                    optionId={option.id}
                    optionEntity={option}
                    onSave={async revisedOption => {
                      const saved = await saveOption(option.id, revisedOption);
                      if (saved)
                        setEditingOptions(e => _.remove(e, { i: option.id }));
                    }}
                    onCancel={() =>
                      setEditingOptions(e => _.remove(e, { i: option.id }))
                    }
                  />
                );
              else if (option.type == "DRINK")
                return (
                  <OptionLine
                    key={`listing-view-option-${option.id}`}
                    optionId={option.id}
                    optionEntity={option}
                    onEdit={() => setEditingOptions(e => [...e, option.id])}
                    onRemove={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to remove this option?"
                        )
                      ) {
                        removeOption(option.id);
                      }
                    }}
                  />
                );
            })}
          </OptionWrapper>
        )}

        {!addingDrinkOption && (
          <OutlineButton
            style={{ marginTop: 15 }}
            title="Add Drink Option"
            onClick={() => setAddingDrinkOption(true)}
          />
        )}

        {addingDrinkOption && (
          <EditOption
            type="DRINK"
            onSave={async newOption => {
              const saved = await addOption(newOption);
              if (saved) setAddingDrinkOption(false);
            }}
            onCancel={() => setAddingDrinkOption(false)}
          />
        )}
      </Section>

      <Section>
        <SectionTitle style={{ marginBottom: 5 }}>Food Menus</SectionTitle>
        <Label>Upload PDFs of your event food menus</Label>

        {foodMenus.length > 0 && (
          <OptionWrapper>
            {foodMenus.map(menu => {
              if (editingMenus.includes(menu.id))
                return (
                  <EditMenu
                    type="FOOD"
                    key={`edit-menu-${menu.id}`}
                    menuId={menu.id}
                    menuEntity={menu}
                    onSave={async revisedMenu => {
                      const saved = await saveMenu(menu.id, revisedMenu);
                      if (saved)
                        setEditingMenus(e => _.remove(e, { i: menu.id }));
                    }}
                    onCancel={() =>
                      setEditingMenus(e => _.remove(e, { i: menu.id }))
                    }
                  />
                );
              else
                return (
                  <MenuLine
                    key={`listing-food-menu-${menu.id}`}
                    menuId={menu.id}
                    menuEntity={menu}
                    onEdit={() => setEditingMenus(e => [...e, menu.id])}
                    onRemove={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to remove this food menu?"
                        )
                      ) {
                        removeMenu(menu.id);
                      }
                    }}
                  />
                );
            })}
          </OptionWrapper>
        )}

        {!addingFoodMenu && (
          <OutlineButton
            style={{ marginTop: 15 }}
            title="Add Food Menu"
            onClick={() => setAddingFoodMenu(true)}
          />
        )}

        {addingFoodMenu && (
          <EditMenu
            type="FOOD"
            onSave={async newMenu => {
              const saved = await addMenu(newMenu);
              if (saved) setAddingFoodMenu(false);
            }}
            onCancel={() => setAddingFoodMenu(false)}
          />
        )}
      </Section>

      <Section>
        <SectionTitle style={{ marginBottom: 5 }}>
          Food Price Estimates
        </SectionTitle>
        <Label>Give ballpark starting cost of your food services</Label>

        {foodOptions.length > 0 && (
          <OptionWrapper>
            {foodOptions.map(option => {
              if (editingOptions.includes(option.id) && option.type == "FOOD")
                return (
                  <EditOption
                    type="FOOD"
                    key={`edit-space-${option.id}`}
                    optionId={option.id}
                    optionEntity={option}
                    onSave={revisedOption => {
                      saveOption(option.id, revisedOption);
                      setEditingOptions(e => _.remove(e, { i: option.id }));
                    }}
                    onCancel={() =>
                      setEditingOptions(e => _.remove(e, { i: option.id }))
                    }
                  />
                );
              else if (option.type === "FOOD")
                return (
                  <OptionLine
                    key={`listing-food-option-${option.id}`}
                    optionId={option.id}
                    optionEntity={option}
                    onEdit={() => setEditingOptions(e => [...e, option.id])}
                    onRemove={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to remove this food option?"
                        )
                      ) {
                        removeOption(option.id);
                      }
                    }}
                  />
                );
            })}
          </OptionWrapper>
        )}

        {!addingFoodOption && (
          <OutlineButton
            style={{ marginTop: 15 }}
            title="Add Food Starting Price"
            onClick={() => setAddingFoodOption(true)}
          />
        )}

        {addingFoodOption && (
          <EditOption
            type="FOOD"
            onSave={async newOption => {
              const saved = await addOption(newOption);
              if (saved) setAddingFoodOption(false);
            }}
            onCancel={() => setAddingFoodOption(false)}
          />
        )}
      </Section>

      <BackButton onClick={back} />
      <NextButton primary={true} loading={loading} onClick={next} />
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: RouteComponentProps) => {
  const { venueId } = ownProps.match.params as { venueId: string };

  return {
    venueEntity: selectors.getVenue(venueId)(state),
    loading: selectors.isUpdatingVenue(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      UpdateVenue
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(Services);
